// ═══════════════════════════════════════════════════════════════════════
// Dispositifs sociaux — « Instances et mandats » d'une organisation (jamais « Gouvernance » : collision avec
// /gouvernance). Organes et fonctions (GET /gouvernance/organisations/:id/organes), occupants à une date
// (…/composition?date=) — une fonction sans occupant est signalée « Fonction à pourvoir » —, mandats paginés
// (…/mandats?statut=). Aucune personne n'est inventée : sans mandat saisi, l'écran montre les fonctions vacantes.
// ═══════════════════════════════════════════════════════════════════════
import { useState } from 'react';
import {
  CalendarSearch, CheckCircle2, CirclePause, CirclePlay, FileText, Gavel, Layers, MoreVertical, Plus, UserPlus, UserX, XCircle,
} from 'lucide-react';
import { Badge, Bouton, BandeauInfo, EtatVide, MenuActions, Squelette, formaterDate } from '../../design/composants';
import { useDs, toArray, libelle, aujourdhuiDakar, NATURES_MANDAT, STATUTS_MANDAT, TYPES_ORGANES } from './api';
import { invaliderEspaces } from './espaces';
import { useLecture, ErreurServeur, AccesRefuseDs, Etat, PaginationDs, FenetreConfirmer, FenetreMotif } from './commun';
import { FenetreAttribuerMandat, FenetreFinMandat, FenetreDetailMandat, FenetreTitulaireA } from './FenetresMandat';
import { FenetreNouvelOrgane, FenetreNouvelleFonction } from './FenetresStructure';

const FILTRES = [['EN_COURS', 'En cours'], ['A_CONFIRMER', 'À confirmer'], ['SUSPENDU', 'Suspendus'], ['TERMINE', 'Terminés'], ['ANNULE', 'Annulés'], ['', 'Tous']];

const periode = (m) => `${formaterDate(m.date_debut)}${m.date_fin_effective ? ` – ${formaterDate(m.date_fin_effective)}`
  : m.date_fin_prevue ? ` – ${formaterDate(m.date_fin_prevue)} (prévue)` : ' – sans fin prévue'}`;

function Occupants({ fonction, occupation }) {
  const occ = toArray(occupation?.occupants);
  if (!occ.length) {
    return fonction.unique_titulaire || occupation?.vacante
      ? <Etat table={{ V: ['alerte', 'Fonction à pourvoir'] }} code="V" />
      : <span className="rh-cellule-secondaire">Aucun membre</span>;
  }
  return (
    <div>
      {occ.map((o) => (
        <div key={o.mandat_id} style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
          <span className="rh-cellule-principale">{o.personne}</span>
          {o.nature !== 'TITULAIRE' && <Badge ton="info">{o.par_interim ? 'par intérim' : libelle(NATURES_MANDAT, o.nature).toLowerCase()}</Badge>}
          {o.statut === 'SUSPENDU' && <Badge ton="alerte">suspendu</Badge>}
          <span className="rh-cellule-secondaire">depuis le {formaterDate(o.date_debut)}{o.date_fin ? `, jusqu'au ${formaterDate(o.date_fin)}` : ''}</span>
        </div>
      ))}
      {occupation?.vacante && <div style={{ marginTop: 4 }}><Etat table={{ V: ['alerte', 'Fonction à pourvoir'] }} code="V" /></div>}
    </div>
  );
}

function CarteOrgane({ organe, occupations, administreStructure, administreMandats, estAujourdhui, onAttribuer, onHistorique, onValiderOrgane, onAjouterFonction }) {
  const fonctions = toArray(organe.fonctions).filter((f) => f.actif !== false);
  return (
    <section className="rh-carte" style={{ padding: 0 }}>
      <div className="rh-carte-entete" style={{ padding: '14px 16px 0' }}>
        <h2 className="rh-carte-titre">
          {organe.libelle}
          {organe.libelle !== libelle(TYPES_ORGANES, organe.type) && <span className="rh-cellule-secondaire" style={{ fontWeight: 400 }}>{libelle(TYPES_ORGANES, organe.type)}</span>}
          {!organe.valide && <Badge ton="alerte" point title="Les mandats de cet organe ne peuvent pas être confirmés avant sa validation.">Organe à valider</Badge>}
        </h2>
        <div style={{ display: 'flex', gap: 4 }}>
          {administreStructure && !organe.valide && <Bouton petit variante="lien" Icone={CheckCircle2} onClick={onValiderOrgane}>Valider l'organe</Bouton>}
          {administreStructure && <Bouton petit variante="lien" Icone={Plus} onClick={onAjouterFonction}>Ajouter une fonction</Bouton>}
        </div>
      </div>
      {!fonctions.length ? (
        <p className="rh-cellule-secondaire" style={{ padding: '8px 16px 16px', margin: 0 }}>Aucune fonction n'est encore définie dans cet organe.</p>
      ) : (
        <div className="rh-conteneur-tableau">
          <table className="rh-tableau">
            <thead><tr><th style={{ width: '30%' }}>Fonction</th><th>Occupant</th><th aria-label="Actions" /></tr></thead>
            <tbody>
              {fonctions.map((f) => (
                <tr key={f.id}>
                  <td>
                    <span className="rh-cellule-principale">{f.libelle}</span>
                    {!f.unique_titulaire && <span className="rh-cellule-secondaire" style={{ display: 'block' }}>Plusieurs membres possibles</span>}
                  </td>
                  <td><Occupants fonction={f} occupation={occupations.get(f.id)} /></td>
                  <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                    {administreMandats && estAujourdhui && <Bouton petit variante="lien" Icone={UserPlus} onClick={() => onAttribuer(f)}>Attribuer</Bouton>}
                    <Bouton petit variante="discret" className="rh-btn-icone" Icone={CalendarSearch} onClick={() => onHistorique(f)}
                      aria-label={`Qui occupait la fonction ${f.libelle} à une date donnée`} title="Qui occupait cette fonction à une date donnée ?" />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

function actionsMandat(m, { administre, ouvrir }) {
  const jour = aujourdhuiDakar();
  const items = [{ libelle: 'Voir le détail', Icone: FileText, onClick: () => ouvrir('detail', m) }];
  if (!administre) return items;
  if (m.statut === 'A_CONFIRMER') {
    items.push({ libelle: 'Confirmer', Icone: CheckCircle2, onClick: () => ouvrir('confirmer', m) });
    items.push({ libelle: 'Annuler', Icone: XCircle, onClick: () => ouvrir('annuler', m), danger: true });
  }
  if (m.statut === 'EN_COURS') {
    items.push({ libelle: 'Suspendre', Icone: CirclePause, onClick: () => ouvrir('suspendre', m) });
    if (m.date_debut > jour) items.push({ libelle: 'Annuler', Icone: XCircle, onClick: () => ouvrir('annuler', m), danger: true });
  }
  if (m.statut === 'SUSPENDU') items.push({ libelle: 'Lever la suspension', Icone: CirclePlay, onClick: () => ouvrir('lever', m) });
  if (['EN_COURS', 'SUSPENDU'].includes(m.statut)) {
    items.push({ libelle: 'Mettre fin au mandat', Icone: UserX, onClick: () => ouvrir('terminer', m) });
    items.push({ libelle: 'Révoquer', Icone: Gavel, onClick: () => ouvrir('revoquer', m), danger: true });
  }
  return items;
}

function TableMandats({ liste, administre, ouvrir }) {
  const elements = toArray(liste);
  return (
    <div className="rh-conteneur-tableau">
      <table className="rh-tableau">
        <thead><tr><th>Fonction</th><th>Personne</th><th>Nature</th><th>Période</th><th>Statut</th><th aria-label="Actions" /></tr></thead>
        <tbody>
          {elements.map((m) => (
            <tr key={m.id}>
              <td><span className="rh-cellule-principale">{m.fonction_libelle}</span><span className="rh-cellule-secondaire" style={{ display: 'block' }}>{m.organe_libelle}</span></td>
              <td>{m.personne}</td>
              <td>{libelle(NATURES_MANDAT, m.nature)}</td>
              <td style={{ whiteSpace: 'nowrap' }}>{periode(m)}</td>
              <td><Etat table={STATUTS_MANDAT} code={m.statut} /></td>
              <td style={{ textAlign: 'right' }}>
                <MenuActions iconeSeule Icone={MoreVertical} libelle={`Actions sur le mandat de ${m.personne}`} items={actionsMandat(m, { administre, ouvrir })} />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function InstancesSection({ organisation, administreStructure, administreMandats, onStructureModifiee }) {
  const ds = useDs();
  const orgId = organisation.id;
  const [date, setDate] = useState(aujourdhuiDakar());
  const [statut, setStatut] = useState('EN_COURS');
  const [page, setPage] = useState(1);
  const [fenetre, setFenetre] = useState(null); // { type, objet }
  const [info, setInfo] = useState('');
  const organes = useLecture(() => ds.organes(orgId), [ds, orgId]);
  const compo = useLecture(() => ds.composition(orgId, date), [ds, orgId, date]);
  const mandats = useLecture(() => ds.mandats(orgId, { page, taille: 25, statut }), [ds, orgId, page, statut]);
  const aConfirmer = useLecture(() => (administreMandats ? ds.mandats(orgId, { page: 1, taille: 1, statut: 'A_CONFIRMER' }) : Promise.resolve(null)), [ds, orgId, administreMandats]);
  const estAujourdhui = date === aujourdhuiDakar();

  const occupations = new Map();
  toArray(compo.donnees?.organes).forEach((o) => toArray(o.fonctions).forEach((f) => occupations.set(f.id, f)));
  const listeOrganes = toArray(organes.donnees).filter((o) => o.etat === 'ACTIF');
  const vacantes = toArray(compo.donnees?.organes).flatMap((o) => toArray(o.fonctions)).filter((f) => f.vacante);

  const toutRecharger = () => { organes.recharger(); compo.recharger(); mandats.recharger(); aConfirmer.recharger(); invaliderEspaces(); };
  const fermer = () => setFenetre(null);
  const apres = (message) => () => { setFenetre(null); setInfo(message); toutRecharger(); };
  const ouvrir = (type, objet) => { setInfo(''); setFenetre({ type, objet }); };

  // Titulaires de la fonction (origine d'un intérim ou d'une délégation), lus dans la composition du jour.
  const titulairesDe = (f) => toArray(occupations.get(f.id)?.occupants)
    .filter((o) => o.nature === 'TITULAIRE' && ['EN_COURS', 'SUSPENDU'].includes(o.statut))
    .map((o) => ({ id: o.mandat_id, personne: o.personne, date_debut: o.date_debut }));

  const lectureRefusee = organes.erreur && organes.erreur.statut === 403;
  if (lectureRefusee) return <AccesRefuseDs erreur={organes.erreur} />;

  return (
    <>
      {info && <BandeauInfo ton="succes">{info}</BandeauInfo>}
      {aConfirmer.donnees?.total > 0 && statut !== 'A_CONFIRMER' && (
        <BandeauInfo ton="info" titre="Mandats à confirmer" action={<Bouton petit onClick={() => { setStatut('A_CONFIRMER'); setPage(1); }}>Voir</Bouton>}>
          {aConfirmer.donnees.total} mandat(s) attendent la confirmation d'une seconde personne (ni celle qui les a enregistrés, ni le titulaire).
        </BandeauInfo>
      )}

      <div className="rh-outils" style={{ justifyContent: 'space-between' }}>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 14 }}>Composition au
            <input type="date" className="rh-champ-saisie" value={date} onChange={(e) => e.target.value && setDate(e.target.value)} />
          </label>
          {!estAujourdhui && <Bouton petit variante="lien" onClick={() => setDate(aujourdhuiDakar())}>Revenir à aujourd'hui</Bouton>}
        </div>
        {administreStructure && <Bouton petit Icone={Layers} onClick={() => ouvrir('organe')}>Nouvel organe</Bouton>}
      </div>
      {!estAujourdhui && <p className="rh-form-aide" style={{ margin: '-6px 0 12px' }}>Composition historique au {formaterDate(date, true)} : les mandats confirmés qui couvraient cette date.</p>}
      {estAujourdhui && vacantes.length > 0 && (
        <BandeauInfo ton="alerte" titre={`${vacantes.length} fonction(s) à pourvoir`}>
          {vacantes.map((f) => f.libelle).join(', ')}. Les actes réservés à ces fonctions ne peuvent pas être accomplis tant qu'aucun mandat n'est confirmé.
        </BandeauInfo>
      )}

      <ErreurServeur erreur={organes.erreur || compo.erreur} />
      {!organes.donnees && !organes.erreur && <Squelette hauteur={120} />}
      {organes.donnees && !listeOrganes.length && (
        <EtatVide Icone={Layers} titre="Aucun organe"
          texte={administreStructure ? 'Créez d\'abord les organes (bureau, commissions), puis leurs fonctions. Les mandats s\'enregistrent ensuite sur pièce.'
            : 'Les organes de cette organisation n\'ont pas encore été créés par son administrateur.'} />
      )}
      {listeOrganes.map((o) => (
        <CarteOrgane key={o.id} organe={o} occupations={occupations} administreStructure={administreStructure} administreMandats={administreMandats}
          estAujourdhui={estAujourdhui} onAttribuer={(f) => ouvrir('attribuer', f)} onHistorique={(f) => ouvrir('titulaire', f)}
          onValiderOrgane={() => ouvrir('valider-organe', o)} onAjouterFonction={() => ouvrir('fonction', o)} />
      ))}

      <section className="rh-carte" style={{ padding: 0, marginTop: 8 }}>
        <div className="rh-carte-entete" style={{ padding: '14px 16px 0' }}><h2 className="rh-carte-titre">Mandats</h2></div>
        <div className="rh-vues rh-vues-ligne" role="group" aria-label="Statut des mandats" style={{ padding: '0 16px' }}>
          {FILTRES.map(([c, l]) => (
            <button key={c || 'tous'} type="button" className="rh-vue" aria-pressed={statut === c} onClick={() => { setStatut(c); setPage(1); }}>{l}</button>
          ))}
        </div>
        {mandats.erreur && <div style={{ padding: '0 16px' }}><ErreurServeur erreur={mandats.erreur} /></div>}
        {!mandats.donnees && !mandats.erreur && <div style={{ padding: 16 }}><Squelette largeur="50%" /></div>}
        {mandats.donnees && !toArray(mandats.donnees).length && (
          <EtatVide titre="Aucun mandat" texte={statut === 'EN_COURS'
            ? 'Aucun mandat n\'est en cours : les fonctions ci-dessus sont à pourvoir. Un mandat s\'enregistre sur pièce (PV, décision) puis est confirmé par une seconde personne.'
            : 'Aucun mandat dans cette catégorie.'} />
        )}
        {mandats.donnees && toArray(mandats.donnees).length > 0 && (
          <>
            <TableMandats liste={mandats.donnees} administre={administreMandats} ouvrir={ouvrir} />
            <PaginationDs donnees={mandats.donnees} onPage={setPage} />
          </>
        )}
      </section>

      {fenetre?.type === 'attribuer' && (
        <FenetreAttribuerMandat organisationId={orgId} fonction={fenetre.objet} titulaires={titulairesDe(fenetre.objet)} onFermer={fermer}
          onFait={apres('Mandat enregistré. Il doit maintenant être confirmé par une autre personne habilitée.')} />
      )}
      {fenetre?.type === 'titulaire' && <FenetreTitulaireA fonction={fenetre.objet} onFermer={fermer} />}
      {fenetre?.type === 'detail' && <FenetreDetailMandat mandatId={fenetre.objet.id} onFermer={fermer} />}
      {fenetre?.type === 'confirmer' && (
        <FenetreConfirmer titre="Confirmer le mandat" Icone={CheckCircle2} libelleBouton="Confirmer"
          texte={`${fenetre.objet.personne} — ${fenetre.objet.fonction_libelle}, ${periode(fenetre.objet)}. Vérifiez la pièce dans le détail du mandat avant de confirmer.`}
          onFermer={fermer} onConfirmer={async () => { await ds.confirmerMandat(fenetre.objet.id); apres('Mandat confirmé.')(); }} />
      )}
      {fenetre?.type === 'lever' && (
        <FenetreConfirmer titre="Lever la suspension" Icone={CirclePlay} libelleBouton="Lever la suspension"
          texte={`${fenetre.objet.personne} retrouve les pouvoirs de la fonction « ${fenetre.objet.fonction_libelle} ».`}
          onFermer={fermer} onConfirmer={async () => { await ds.leverMandat(fenetre.objet.id); apres('Suspension levée.')(); }} />
      )}
      {fenetre?.type === 'annuler' && (
        <FenetreMotif titre="Annuler le mandat" Icone={XCircle} danger libelleBouton="Annuler le mandat"
          aide="Seul un mandat à confirmer ou pas encore commencé s'annule. Un mandat commencé se termine."
          onFermer={fermer} onConfirmer={async (motif) => { await ds.annulerMandat(fenetre.objet.id, motif); apres('Mandat annulé.')(); }} />
      )}
      {fenetre?.type === 'suspendre' && (
        <FenetreMotif titre="Suspendre le mandat" Icone={CirclePause} libelleBouton="Suspendre"
          aide="Pendant la suspension, la personne n'accomplit plus aucun acte de la fonction."
          onFermer={fermer} onConfirmer={async (motif) => { await ds.suspendreMandat(fenetre.objet.id, motif); apres('Mandat suspendu.')(); }} />
      )}
      {(fenetre?.type === 'terminer' || fenetre?.type === 'revoquer') && (
        <FenetreFinMandat mandat={fenetre.objet} revocation={fenetre.type === 'revoquer'} onFermer={fermer}
          onFait={apres(fenetre.type === 'revoquer' ? 'Mandat révoqué.' : 'Mandat terminé.')} />
      )}
      {fenetre?.type === 'organe' && (
        <FenetreNouvelOrgane organisationId={orgId} onFermer={fermer} onCree={() => { apres('Organe créé.')(); onStructureModifiee(); }} />
      )}
      {fenetre?.type === 'fonction' && (
        <FenetreNouvelleFonction organisation={organisation} organe={fenetre.objet} onFermer={fermer} onCree={apres('Fonction ajoutée : elle est à pourvoir.')} />
      )}
      {fenetre?.type === 'valider-organe' && (
        <FenetreConfirmer titre="Valider l'organe" Icone={CheckCircle2} libelleBouton="Valider"
          texte={`« ${fenetre.objet.libelle} » : la validation revient à un administrateur autre que celui qui l'a créé.`}
          onFermer={fermer} onConfirmer={async () => { await ds.validerOrgane(fenetre.objet.id); apres('Organe validé.')(); }} />
      )}
    </>
  );
}
